import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { inviteAcceptSchema, resetConfirmSchema, resetRequestSchema } from '@agency/contracts';
import { Button, Notice, Skeleton, TextField } from '@agency/ui';
import { ApiClientError, get, post } from '../../lib/api.ts';
import { AuthCard } from './auth-card.tsx';

const msg = (e: unknown) => (e instanceof ApiClientError ? e.message : 'Something went wrong. Try again.');

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  if (state === 'sent') {
    return (
      <AuthCard title="Check your email">
        <Notice tone="success" title="Request received">If an account exists for that address, a reset link is on its way. The link works once and expires soon.</Notice>
        <p><Link to="/login">Back to sign in</Link></p>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="Reset your password" lead="Enter your work email and we’ll send a link to choose a new password.">
      {error ? <Notice tone="error" title="Could not send the request">{error}</Notice> : null}
      <form noValidate onSubmit={async (e) => {
        e.preventDefault();
        const p = resetRequestSchema.safeParse({ email });
        if (!p.success) { setError('Enter a valid email address.'); return; }
        setState('busy'); setError(null);
        try { await post('/auth/password-reset/request', p.data); setState('sent'); } catch (err) { setError(msg(err)); setState('idle'); }
      }}>
        <TextField label="Email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button type="submit" variant="primary" loading={state === 'busy'}>Send reset link</Button>
      </form>
      <p><Link to="/login">Back to sign in</Link></p>
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const token = useSearchParams()[0].get('token') ?? '';
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (done) {
    return (
      <AuthCard title="Password updated">
        <Notice tone="success" title="You can sign in now">Your password was changed and every other session was ended.</Notice>
        <Link className="ag-btn ag-btn--primary" style={{ textDecoration: 'none', justifySelf: 'start' }} to="/login">Go to sign in</Link>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="Choose a new password" lead="Use at least 12 characters. A passphrase of several words works well.">
      {!token ? <Notice tone="error" title="This link is incomplete">Open the link from the email again, or <Link to="/forgot-password">request a new one</Link>.</Notice> : null}
      {error ? <Notice tone="error" title="Could not change the password">{error} <Link to="/forgot-password">Request a new link</Link></Notice> : null}
      <form noValidate onSubmit={async (e) => {
        e.preventDefault();
        const p = resetConfirmSchema.safeParse({ token, password });
        if (!p.success) { setError(p.error.issues[0]?.message ?? 'Check the password.'); return; }
        setBusy(true); setError(null);
        try { await post('/auth/password-reset/confirm', p.data); setDone(true); } catch (err) { setError(msg(err)); } finally { setBusy(false); }
      }}>
        <TextField label="New password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" variant="primary" loading={busy} disabled={!token}>Change password</Button>
      </form>
    </AuthCard>
  );
}

export function AcceptInvitePage() {
  const token = useSearchParams()[0].get('token') ?? '';
  const preview = useQuery<{ email: string; roleName: string }, ApiClientError>({ queryKey: ['invite-preview', token], queryFn: () => get(`/auth/invites/preview?token=${encodeURIComponent(token)}`), enabled: token.length > 0, retry: false });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (done) {
    return (
      <AuthCard title="Welcome aboard">
        <Notice tone="success" title="Your account is ready">Sign in with {preview.data?.email} and the password you just chose.</Notice>
        <Link className="ag-btn ag-btn--primary" style={{ textDecoration: 'none', justifySelf: 'start' }} to="/login">Go to sign in</Link>
      </AuthCard>
    );
  }
  if (!token || preview.isError) {
    return (
      <AuthCard title="This invitation can’t be used">
        <Notice tone="error" title="Invalid or expired">Invitations expire and work once. Ask the person who invited you to send a new one.</Notice>
        <p><Link to="/login">Go to sign in</Link></p>
      </AuthCard>
    );
  }
  if (preview.isLoading) return <AuthCard title="Accept invitation"><div role="status"><span className="ag-visually-hidden">Checking invitation…</span><Skeleton width="100%" height={120} /></div></AuthCard>;
  return (
    <AuthCard title="Accept invitation" lead={<>You’re joining as <strong>{preview.data!.roleName}</strong> with {preview.data!.email}.</>}>
      {error ? <Notice tone="error" title="Could not create your account">{error}</Notice> : null}
      <form noValidate onSubmit={async (e) => {
        e.preventDefault();
        const p = inviteAcceptSchema.safeParse({ token, name, password });
        if (!p.success) { setError(p.error.issues[0]?.message ?? 'Check the form.'); return; }
        setBusy(true); setError(null);
        try { await post('/auth/invites/accept', p.data); setDone(true); } catch (err) { setError(msg(err)); } finally { setBusy(false); }
      }}>
        <TextField label="Your name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label="Password" type="password" autoComplete="new-password" required hint="At least 12 characters." value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" variant="primary" loading={busy}>Create account</Button>
      </form>
    </AuthCard>
  );
}
